/**
 * Command Engine — the single output channel of the AI Design Engine.
 *
 * Every design decision the engine makes is expressed as a structured
 * editor command. Commands never draw anything; the iVisora editor will
 * execute them later. This module owns:
 *
 *   - the command registry (extensible: registerCommand adds new verbs)
 *   - command creation + param validation
 *   - CommandBuffer: an ordered, serializable command stream
 *
 * A command object looks like:
 *   { id, command, target, params }
 */

let commandSeq = 0;

/** Deterministic-enough unique id for a command within one generation run. */
export const nextCommandId = (prefix = 'cmd') => `${prefix}_${++commandSeq}`;

/** Reset the id counter (used by tests for stable snapshots). */
export const resetCommandIds = () => { commandSeq = 0; };

// ── Param schema validation ──────────────────────────────────────────────────
// Minimal, dependency-free schema: { name: { type, required, enum, min, max } }

const TYPE_CHECKS = {
  string: (v) => typeof v === 'string',
  number: (v) => typeof v === 'number' && Number.isFinite(v),
  boolean: (v) => typeof v === 'boolean',
  object: (v) => v !== null && typeof v === 'object' && !Array.isArray(v),
  array: (v) => Array.isArray(v),
};

const validateParams = (schema, params) => {
  const errors = [];
  Object.entries(schema).forEach(([key, rule]) => {
    const value = params[key];
    if (value === undefined || value === null) {
      if (rule.required) errors.push(`missing required param "${key}"`);
      return;
    }
    if (rule.type && !TYPE_CHECKS[rule.type]?.(value)) {
      errors.push(`param "${key}" must be a ${rule.type}`);
      return;
    }
    if (rule.enum && !rule.enum.includes(value)) {
      errors.push(`param "${key}" must be one of: ${rule.enum.join(', ')}`);
    }
    if (rule.min !== undefined && value < rule.min) {
      errors.push(`param "${key}" must be >= ${rule.min}`);
    }
    if (rule.max !== undefined && value > rule.max) {
      errors.push(`param "${key}" must be <= ${rule.max}`);
    }
  });
  return errors;
};

// ── Registry ─────────────────────────────────────────────────────────────────

const registry = new Map();

/**
 * Register a command definition.
 * def = { name, category, description, params: schema }
 */
export const registerCommand = (def) => {
  if (!def?.name) throw new Error('registerCommand: definition needs a name');
  registry.set(def.name, { params: {}, category: 'general', ...def });
  return def.name;
};

export const getCommandDefinition = (name) => registry.get(name) || null;
export const listCommands = () => [...registry.keys()];

/**
 * Create a validated command object. Throws on unknown command or bad params
 * so composition bugs surface at generation time, not in the editor.
 */
export const createCommand = (name, params = {}, target = null) => {
  const def = registry.get(name);
  if (!def) throw new Error(`Unknown command "${name}"`);
  const errors = validateParams(def.params, params);
  if (errors.length) {
    throw new Error(`Invalid params for "${name}": ${errors.join('; ')}`);
  }
  return { id: nextCommandId(), command: name, target, params: { ...params } };
};

// ── Command buffer ───────────────────────────────────────────────────────────

/** Ordered command stream with grouping/serialization helpers. */
export class CommandBuffer {
  constructor() {
    this.commands = [];
  }

  push(name, params, target) {
    const cmd = createCommand(name, params, target);
    this.commands.push(cmd);
    return cmd;
  }

  pushCommand(cmd) {
    this.commands.push(cmd);
    return cmd;
  }

  /** Wrap already-emitted command ids into a group command. */
  group(commandIds, name = 'group') {
    return this.push('group', { members: commandIds, name });
  }

  byCommand(name) {
    return this.commands.filter((c) => c.command === name);
  }

  toJSON() {
    return this.commands;
  }

  serialize() {
    return JSON.stringify(this.commands, null, 2);
  }
}

// ── Built-in command set ─────────────────────────────────────────────────────
// Shared geometry schema for placeable objects.

const BOX = {
  x: { type: 'number', required: true },
  y: { type: 'number', required: true },
  width: { type: 'number', required: true, min: 0 },
  height: { type: 'number', required: true, min: 0 },
};

const TEXT_STYLE = {
  text: { type: 'string', required: true },
  fontFamily: { type: 'string' },
  fontSize: { type: 'number', min: 1 },
  fontWeight: { type: 'number' },
  letterSpacing: { type: 'number' },
  lineHeight: { type: 'number' },
  color: { type: 'string' },
  align: { type: 'string', enum: ['left', 'center', 'right'] },
};

const BUILT_IN_COMMANDS = [
  { name: 'createCanvas', category: 'structure', description: 'Define the canvas', params: {
    width: { type: 'number', required: true, min: 1 },
    height: { type: 'number', required: true, min: 1 },
    background: { type: 'string' },
  } },
  { name: 'createSection', category: 'structure', description: 'Logical layout section', params: {
    ...BOX, role: { type: 'string', required: true },
  } },
  { name: 'createHero', category: 'structure', description: 'Hero focal area', params: { ...BOX } },
  { name: 'createHeadline', category: 'text', description: 'Primary heading', params: { ...BOX, ...TEXT_STYLE } },
  { name: 'createSubheadline', category: 'text', description: 'Secondary heading', params: { ...BOX, ...TEXT_STYLE } },
  { name: 'createParagraph', category: 'text', description: 'Body copy', params: { ...BOX, ...TEXT_STYLE } },
  { name: 'createCTA', category: 'component', description: 'Call-to-action button', params: {
    ...BOX, ...TEXT_STYLE, background: { type: 'string' }, borderRadius: { type: 'number', min: 0 },
  } },
  { name: 'createBadge', category: 'component', description: 'Small emphasis badge', params: {
    ...BOX, ...TEXT_STYLE, background: { type: 'string' }, borderRadius: { type: 'number', min: 0 },
  } },
  { name: 'createImagePlaceholder', category: 'media', description: 'Reserved image zone', params: {
    ...BOX, fit: { type: 'string', enum: ['cover', 'contain'] }, label: { type: 'string' },
  } },
  { name: 'createShape', category: 'shape', description: 'Geometric shape', params: {
    ...BOX, shape: { type: 'string', required: true }, fill: { type: 'string' },
    rotation: { type: 'number' }, opacity: { type: 'number', min: 0, max: 1 },
  } },
  { name: 'createBlob', category: 'decoration', description: 'Organic blob decoration', params: {
    ...BOX, fill: { type: 'string' }, seed: { type: 'number' }, opacity: { type: 'number', min: 0, max: 1 },
  } },
  { name: 'createWave', category: 'decoration', description: 'Wave divider decoration', params: {
    ...BOX, fill: { type: 'string' }, amplitude: { type: 'number' }, direction: { type: 'string', enum: ['top', 'bottom'] },
  } },
  { name: 'createRibbon', category: 'decoration', description: 'Ribbon banner', params: {
    ...BOX, fill: { type: 'string' }, text: { type: 'string' },
  } },
  { name: 'createPattern', category: 'decoration', description: 'Repeating pattern fill', params: {
    ...BOX, pattern: { type: 'string', required: true }, color: { type: 'string' }, opacity: { type: 'number', min: 0, max: 1 },
  } },
  { name: 'createGradient', category: 'style', description: 'Gradient fill layer', params: {
    ...BOX, from: { type: 'string', required: true }, to: { type: 'string', required: true }, angle: { type: 'number' },
  } },
  { name: 'createShadow', category: 'style', description: 'Attach shadow to an object', params: {
    targetId: { type: 'string', required: true }, blur: { type: 'number', min: 0 },
    offsetX: { type: 'number' }, offsetY: { type: 'number' }, color: { type: 'string' },
  } },
  { name: 'createDivider', category: 'component', description: 'Horizontal divider line', params: {
    ...BOX, color: { type: 'string' }, thickness: { type: 'number', min: 0 },
  } },
  { name: 'createFooter', category: 'structure', description: 'Footer area', params: { ...BOX } },
  { name: 'group', category: 'operation', description: 'Group commands into a unit', params: {
    members: { type: 'array', required: true }, name: { type: 'string' },
  } },
  { name: 'align', category: 'operation', description: 'Align objects', params: {
    targets: { type: 'array', required: true },
    horizontal: { type: 'string', enum: ['left', 'center', 'right'] },
    vertical: { type: 'string', enum: ['top', 'middle', 'bottom'] },
  } },
  { name: 'duplicate', category: 'operation', description: 'Duplicate an object', params: {
    targetId: { type: 'string', required: true }, offsetX: { type: 'number' }, offsetY: { type: 'number' },
  } },
  { name: 'lock', category: 'operation', description: 'Lock an object', params: {
    targetId: { type: 'string', required: true },
  } },
  { name: 'exportTemplate', category: 'operation', description: 'Finalize the template', params: {
    name: { type: 'string' }, format: { type: 'string', enum: ['template_json'] },
  } },
];

BUILT_IN_COMMANDS.forEach(registerCommand);

export const BUILT_IN_COMMAND_NAMES = BUILT_IN_COMMANDS.map((c) => c.name);
